import React, { useState, useEffect, useRef } from 'react';
import { ref, onValue, push, update, get } from 'firebase/database';
import { db } from '../../firebase';
import { ChatMessage, MessageThread, Student } from '../../types';

const fmt = (ts: number) =>
  new Date(ts).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }) +
  ' · ' +
  new Date(ts).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short' });

const AdminMessagesTab: React.FC = () => {
  const [threads, setThreads] = useState<MessageThread[]>([]);
  const [selectedUid, setSelectedUid] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [students, setStudents] = useState<Student[]>([]);
  const [showNewMsg, setShowNewMsg] = useState(false);
  const [newMsgUid, setNewMsgUid] = useState('');
  const [newMsgText, setNewMsgText] = useState('');
  const [sendingNew, setSendingNew] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const u1 = onValue(ref(db, 'messageThreads'), snap => {
      const d = snap.val();
      setThreads(d
        ? Object.entries(d).map(([uid, v]) => ({ uid, ...(v as Omit<MessageThread, 'uid'>) }))
            .sort((a, b) => b.lastAt - a.lastAt)
        : []);
    });
    const u2 = onValue(ref(db, 'students'), snap => {
      const d = snap.val();
      setStudents(d ? Object.values(d) as Student[] : []);
    });
    return () => { u1(); u2(); };
  }, []);

  useEffect(() => {
    if (!selectedUid) return;
    const unsub = onValue(ref(db, `messages/${selectedUid}`), snap => {
      const d = snap.val();
      const msgs: ChatMessage[] = d
        ? Object.entries(d).map(([id, v]) => ({ id, ...(v as ChatMessage) })).sort((a, b) => a.createdAt - b.createdAt)
        : [];
      setMessages(msgs);
      update(ref(db, `messageThreads/${selectedUid}`), { unreadByAdmin: false });
    });
    return unsub;
  }, [selectedUid]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const openThread = (uid: string) => {
    setSelectedUid(uid);
    setReply('');
    setShowNewMsg(false);
  };

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reply.trim() || !selectedUid) return;
    setSending(true);

    const newMsg: Omit<ChatMessage, 'id'> = {
      from: 'admin',
      text: reply.trim(),
      createdAt: Date.now(),
      read: true,
    };

    const threadSnap = await get(ref(db, `messageThreads/${selectedUid}`));
    const thread = threadSnap.val() as MessageThread | null;

    await push(ref(db, `messages/${selectedUid}`), newMsg);
    await update(ref(db, `messageThreads/${selectedUid}`), {
      lastMessage: newMsg.text,
      lastAt: newMsg.createdAt,
      unreadByAdmin: false,
      studentName: thread?.studentName || 'Öğrenci',
      studentEmail: thread?.studentEmail || '',
    });

    setReply('');
    setSending(false);
  };

  const handleNewMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMsgUid || !newMsgText.trim()) return;
    const student = students.find(s => s.uid === newMsgUid);
    if (!student) return;
    setSendingNew(true);

    const newMsg: Omit<ChatMessage, 'id'> = {
      from: 'admin',
      text: newMsgText.trim(),
      createdAt: Date.now(),
      read: true,
    };

    await push(ref(db, `messages/${newMsgUid}`), newMsg);
    await update(ref(db, `messageThreads/${newMsgUid}`), {
      uid: newMsgUid,
      studentName: student.fullName,
      studentEmail: student.email,
      lastMessage: newMsg.text,
      lastAt: newMsg.createdAt,
      unreadByAdmin: false,
    });

    setShowNewMsg(false);
    setNewMsgUid('');
    setNewMsgText('');
    setSelectedUid(newMsgUid);
    setSendingNew(false);
  };

  const selectedThread = threads.find(t => t.uid === selectedUid);

  // Students who don't have a thread yet
  const threadUids = new Set(threads.map(t => t.uid));
  const studentsWithoutThread = students.filter(s => !threadUids.has(s.uid));

  return (
    <>
      <h2 className="admin-page-title">Öğrenci Mesajları</h2>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <p className="admin-page-sub" style={{ margin: 0 }}>
          {threads.filter(t => t.unreadByAdmin).length} okunmamış mesaj
        </p>
        <button
          className="a-btn a-btn-add"
          style={{ padding: '5px 14px', fontSize: '0.82rem', margin: 0 }}
          onClick={() => { setShowNewMsg(v => !v); setSelectedUid(null); }}
        >
          + Yeni Mesaj
        </button>
      </div>

      {showNewMsg && (
        <div className="admin-card" style={{ marginBottom: 16, padding: 16 }}>
          <div className="admin-card-header"><h3>Öğrenciye Mesaj Gönder</h3></div>
          <form onSubmit={handleNewMessage} style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '12px 0 0' }}>
            <select
              className="a-select"
              value={newMsgUid}
              onChange={e => setNewMsgUid(e.target.value)}
              required
            >
              <option value="">Öğrenci Seçin...</option>
              {threads.map(t => (
                <option key={t.uid} value={t.uid}>{t.studentName} ({t.studentEmail})</option>
              ))}
              {studentsWithoutThread.length > 0 && (
                <optgroup label="─── Yeni Konuşma ───">
                  {studentsWithoutThread.map(s => (
                    <option key={s.uid} value={s.uid}>{s.fullName} ({s.email})</option>
                  ))}
                </optgroup>
              )}
            </select>
            <textarea
              className="a-search"
              style={{ minHeight: 80, resize: 'vertical', fontFamily: 'inherit' }}
              placeholder="Mesajınızı yazın..."
              value={newMsgText}
              onChange={e => setNewMsgText(e.target.value)}
              required
            />
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="submit" className="a-btn a-btn-save" disabled={sendingNew || !newMsgUid || !newMsgText.trim()}>
                {sendingNew ? 'Gönderiliyor...' : 'Gönder'}
              </button>
              <button type="button" className="a-btn a-btn-cancel" onClick={() => { setShowNewMsg(false); setNewMsgUid(''); setNewMsgText(''); }}>
                İptal
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="a-msg-layout">
        {/* Thread List */}
        <div className="a-msg-threads">
          {threads.length === 0 ? (
            <p className="a-empty" style={{ padding: 16 }}>Henüz mesaj bulunmamaktadır.</p>
          ) : threads.map(t => (
            <div
              key={t.uid}
              className={`a-msg-thread-item ${selectedUid === t.uid ? 'active' : ''} ${t.unreadByAdmin ? 'unread' : ''}`}
              onClick={() => openThread(t.uid)}
            >
              <div className="a-msg-thread-avatar">{t.studentName?.charAt(0)?.toUpperCase() || '?'}</div>
              <div className="a-msg-thread-info">
                <div className="a-msg-thread-name">
                  {t.studentName}
                  {t.unreadByAdmin && <span className="a-unread-dot" />}
                </div>
                <div className="a-msg-thread-preview">{t.lastMessage}</div>
              </div>
              <div className="a-msg-thread-time">
                {new Date(t.lastAt).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short' })}
              </div>
            </div>
          ))}
        </div>

        {/* Chat Area */}
        <div className="a-msg-chat">
          {!selectedUid ? (
            <div className="a-msg-empty">
              <p>Sol listeden bir öğrenci seçin veya "Yeni Mesaj" ile konuşma başlatın.</p>
            </div>
          ) : (
            <>
              <div className="a-msg-chat-header">
                <div className="a-msg-chat-name">{selectedThread?.studentName}</div>
                <div className="a-msg-chat-email">{selectedThread?.studentEmail}</div>
              </div>

              <div className="a-msg-chat-body">
                {messages.length === 0 && (
                  <p className="a-empty" style={{ padding: 16 }}>Henüz mesaj yok.</p>
                )}
                {messages.map(m => (
                  <div key={m.id} className={`a-bubble-wrap ${m.from === 'admin' ? 'a-bubble-right' : 'a-bubble-left'}`}>
                    <div className={`a-bubble ${m.from === 'admin' ? 'a-bubble-admin' : 'a-bubble-student'}`}>
                      <p>{m.text}</p>
                      <span className="a-bubble-time">{fmt(m.createdAt)}</span>
                    </div>
                  </div>
                ))}
                <div ref={bottomRef} />
              </div>

              <form onSubmit={handleSend} className="a-msg-reply-form">
                <input
                  className="a-search"
                  value={reply}
                  onChange={e => setReply(e.target.value)}
                  placeholder="Yanıtınızı yazın..."
                  disabled={sending}
                />
                <button type="submit" className="a-btn a-btn-save" disabled={sending || !reply.trim()}>
                  {sending ? '...' : 'Gönder'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default AdminMessagesTab;
